// Work out which Supabase the admin is actually talking to, and where a human
// can go to look at it.
//
// The link is derived from VITE_SUPABASE_URL rather than from the deployment
// environment, because that variable is the ground truth: it is the database
// this build reads and writes. The environment badge is reported separately and
// deliberately NOT used to pick the link — if the two ever disagree, you want to
// see the disagreement, not have it papered over.

export type SupabaseKind = 'local' | 'hosted' | 'unknown';

/** Vercel calls the pre-production environment "preview"; we call it staging. */
export type DeployEnv = 'production' | 'staging' | 'local';

export interface SupabaseInstance {
	kind: SupabaseKind;
	/** The API URL this build is configured with, as given. */
	apiUrl: string | null;
	/** Project ref for a hosted project — the `abcd` in `abcd.supabase.co`. */
	projectRef: string | null;
	/** Where the "open" button goes, or null when we can't work it out. */
	consoleUrl: string | null;
	/** What that destination is called, so the button doesn't lie about it. */
	consoleLabel: string;
}

// Studio's port in `storefront/supabase/config.toml` ([studio] port = 54323).
// Hardcoded because the admin app has no way to read the CLI's config.
const LOCAL_STUDIO_PORT = 54323;

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

export function describeSupabase(rawUrl: string | undefined | null): SupabaseInstance {
	const apiUrl = rawUrl?.trim() ? rawUrl.trim().replace(/\/$/, '') : null;

	const unknown: SupabaseInstance = {
		kind: 'unknown',
		apiUrl,
		projectRef: null,
		consoleUrl: null,
		consoleLabel: 'Supabase'
	};

	if (!apiUrl) return unknown;

	let parsed: URL;
	try {
		parsed = new URL(apiUrl);
	} catch {
		return unknown;
	}

	if (LOCAL_HOSTS.has(parsed.hostname)) {
		// Keep whichever loopback spelling is configured. They are the same host,
		// but a link that matches what you set is less confusing to follow.
		return {
			kind: 'local',
			apiUrl,
			projectRef: null,
			consoleUrl: `${parsed.protocol}//${parsed.hostname}:${LOCAL_STUDIO_PORT}`,
			consoleLabel: 'Supabase Studio'
		};
	}

	// Hosted projects are always <ref>.supabase.co. Match the suffix rather than
	// splitting on the first dot, so an unrelated host ending in something
	// similar doesn't produce a bogus dashboard link.
	const hosted = /^([a-z0-9-]+)\.supabase\.(?:co|in)$/i.exec(parsed.hostname);
	if (hosted) {
		const projectRef = hosted[1];
		return {
			kind: 'hosted',
			apiUrl,
			projectRef,
			consoleUrl: `https://supabase.com/dashboard/project/${projectRef}`,
			consoleLabel: 'Supabase dashboard'
		};
	}

	// Self-hosted or a custom domain: we know where the API is but cannot guess
	// where its console lives, so offer no link rather than a broken one.
	return unknown;
}

export function resolveDeployEnv(vercelEnv: string | undefined, isDev: boolean): DeployEnv {
	if (vercelEnv === 'production') return 'production';
	if (vercelEnv === 'preview') return 'staging';
	if (isDev) return 'local';
	// A build with no Vercel env that isn't the dev server — treat as local
	// rather than claiming production, which is the costlier thing to get wrong.
	return 'local';
}

export const DEPLOY_ENV_LABEL: Record<DeployEnv, string> = {
	production: 'Production',
	staging: 'Staging (Vercel preview)',
	local: 'Local'
};

/**
 * True when the local dev server is pointed at a real hosted database.
 *
 * Normal setups are local→local and deployed→hosted. Local→hosted means edits
 * made while developing land in a database someone else is relying on, which is
 * worth saying out loud. Deployed→hosted is not flagged: that is just staging
 * and production working as intended.
 */
export function isLocalPointedAtHosted(instance: SupabaseInstance, env: DeployEnv): boolean {
	return env === 'local' && instance.kind === 'hosted';
}
